import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TextField from "../../Components/TextField/TextField";
import Button from "../../Components/Button/Button";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import api from "../../services/api";
import "./ProductList.css";

// Utility: YYYY-MM-DD from a Date (UTC-safe for <input type="date">).
const toDateInputValue = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Days remaining vs. now, rounded. Negative = overdue.
const daysUntil = (iso) => {
  if (!iso) return null;
  const end = new Date(iso);
  if (isNaN(end.getTime())) return null;
  return Math.round((end.getTime() - Date.now()) / 86400000);
};

// Admin-only inventory overview.
// Shows, for every catalog product, how many physical tags exist and where
// they are distributed across Location → Warehouse → Room. Two sub-tabs:
//   - "By Product"  (default): one row per product with drill-down tree.
//   - "By Location"          : the same data pivoted hierarchy-first.
// Also surfaces `unassigned` tags (no room) and `orphans` (tag EPC not in
// the catalog) so the admin can clean them up.

const STATE_ORDER = ["READY", "BLOCKED", "ON_DELIVERY", "PENDING_REQUEST"];
const STATE_STYLE = {
  READY:           { label: "Ready",         bg: "#DCFCE7", color: "#166534", border: "#86EFAC" },
  BLOCKED:         { label: "Blocked",       bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" },
  ON_DELIVERY:     { label: "On Delivery",   bg: "#DBEAFE", color: "#1E40AF", border: "#93C5FD" },
  PENDING_REQUEST: { label: "Pending",       bg: "#FEF3C7", color: "#92400E", border: "#FCD34D" },
};

const formatPrice = (n) => `$${Number(n || 0).toFixed(2)}`;

const StateBadges = ({ states }) => {
  if (!states) return null;
  const entries = STATE_ORDER.filter((s) => (states[s] || 0) > 0);
  if (entries.length === 0) {
    return <span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>—</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {entries.map((s) => {
        const style = STATE_STYLE[s];
        return (
          <span
            key={s}
            className="state-badge"
            style={{
              backgroundColor: style.bg,
              color: style.color,
              border: `1px solid ${style.border}`,
              fontSize: 11,
              padding: "2px 8px",
            }}
            title={`${style.label}: ${states[s]}`}
          >
            {style.label}: <strong style={{ marginLeft: 3 }}>{states[s]}</strong>
          </span>
        );
      })}
    </div>
  );
};

const SummaryCard = ({ icon, label, value, accent = "var(--accent)" }) => (
  <div className="pl-summary-card">
    <div className="pl-summary-icon" style={{ color: accent, backgroundColor: `${accent}14` }}>
      <i className={`fa ${icon}`} aria-hidden />
    </div>
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span className="pl-summary-label">{label}</span>
      <span className="pl-summary-value">{value}</span>
    </div>
  </div>
);

// --------------- By-Product view: expandable product table ------------------

const LocationSubtree = ({ byLocation }) => {
  if (!byLocation || byLocation.length === 0) {
    return <p className="pl-subtle">No physical tags recorded for this product.</p>;
  }
  return (
    <div className="pl-tree">
      {byLocation.map((loc, li) => (
        <details key={loc.locationId || `loc-${li}`} className="pl-tree-node" open>
          <summary>
            <i className="fa fa-map-marker pl-tree-icon" aria-hidden />
            <span className="pl-tree-name">{loc.locationName}</span>
            <span className="pl-tree-count">{loc.count}</span>
          </summary>
          <div className="pl-tree-children">
            {loc.byWarehouse.map((wh, wi) => (
              <details key={wh.warehouseId || `wh-${wi}`} className="pl-tree-node" open>
                <summary>
                  <i className="fa fa-building pl-tree-icon" aria-hidden />
                  <span className="pl-tree-name">{wh.warehouseName}</span>
                  <span className="pl-tree-count">{wh.count}</span>
                </summary>
                <div className="pl-tree-children">
                  {wh.byRoom.map((room) => (
                    <div key={room.rid} className="pl-tree-leaf">
                      <i className="fa fa-cube pl-tree-icon" aria-hidden />
                      <span className="pl-tree-name">{room.roomTitle}</span>
                      <span className="pl-tree-count">{room.count}</span>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
};

const ByProductView = ({ products, searchTerm, onManageExpiry }) => {
  const [expanded, setExpanded] = useState(() => new Set());

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.epc || "").toLowerCase().includes(q)
    );
  }, [products, searchTerm]);

  const toggle = useCallback((epc) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(epc)) next.delete(epc);
      else next.add(epc);
      return next;
    });
  }, []);

  if (filtered.length === 0) {
    return (
      <div className="empty-state">
        <i className="fa fa-search empty-state-icon" />
        <p>{searchTerm ? `No products match "${searchTerm}"` : "No products in the catalog yet."}</p>
      </div>
    );
  }

  return (
    <table className="tags-table">
      <thead>
        <tr>
          <th style={{ width: 32 }}></th>
          <th>Image</th>
          <th>Name</th>
          <th>EPC</th>
          <th>Price</th>
          <th>Total</th>
          <th>State breakdown</th>
          <th style={{ width: 160 }}>Expiry</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((p) => {
          const open = expanded.has(p.epc);
          return (
            <Fragment key={p.epc}>
              <tr
                onClick={() => toggle(p.epc)}
                className="pl-row pl-row-clickable"
                title={open ? "Click to collapse" : "Click to expand location breakdown"}
              >
                <td>
                  <i
                    className={`fa fa-chevron-${open ? "down" : "right"}`}
                    style={{ color: "var(--text-tertiary)" }}
                    aria-hidden
                  />
                </td>
                <td>
                  {p.productImgLink ? (
                    <img
                      src={p.productImgLink}
                      alt={p.name}
                      style={{ width: 42, height: 42, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border-primary)" }}
                    />
                  ) : (
                    <span style={{ color: "var(--text-tertiary)" }}>—</span>
                  )}
                </td>
                <td><span className="epc-cell fw-bold">{p.name}</span></td>
                <td><span className="tag-badge" style={{ fontFamily: "monospace" }}>{p.epc}</span></td>
                <td><span className="tag-badge">{formatPrice(p.price)}</span></td>
                <td>
                  <span
                    className="tag-badge"
                    style={{
                      backgroundColor: p.total > 0 ? "var(--accent-subtle)" : "var(--bg-tertiary)",
                      color: p.total > 0 ? "var(--accent)" : "var(--text-tertiary)",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {p.total}
                  </span>
                </td>
                <td><StateBadges states={p.states} /></td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="pl-expiry-btn"
                    disabled={p.total === 0}
                    onClick={() => onManageExpiry?.(p)}
                    title={p.total === 0 ? "No physical tags to manage" : "Manage end dates for every tag"}
                  >
                    <i className="fa fa-calendar" aria-hidden /> Manage
                  </button>
                </td>
              </tr>
              {open && (
                <tr className="pl-subrow">
                  <td colSpan={8}>
                    <LocationSubtree byLocation={p.byLocation} />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

// --------------- By-Location view: location → warehouse → room → products ---

// Pivot the products[].byLocation array into a location-first tree so the
// same payload powers both views.
const buildLocationTree = (products) => {
  const locMap = new Map();
  for (const p of products) {
    for (const loc of p.byLocation || []) {
      const locKey = loc.locationId || `__${loc.locationName}__`;
      let locEntry = locMap.get(locKey);
      if (!locEntry) {
        locEntry = {
          locationId: loc.locationId,
          locationName: loc.locationName,
          total: 0,
          warehouses: new Map(),
        };
        locMap.set(locKey, locEntry);
      }
      locEntry.total += loc.count;
      for (const wh of loc.byWarehouse || []) {
        const whKey = wh.warehouseId || `__${wh.warehouseName}__`;
        let whEntry = locEntry.warehouses.get(whKey);
        if (!whEntry) {
          whEntry = {
            warehouseId: wh.warehouseId,
            warehouseName: wh.warehouseName,
            total: 0,
            rooms: new Map(),
          };
          locEntry.warehouses.set(whKey, whEntry);
        }
        whEntry.total += wh.count;
        for (const room of wh.byRoom || []) {
          let roomEntry = whEntry.rooms.get(room.rid);
          if (!roomEntry) {
            roomEntry = {
              rid: room.rid,
              roomTitle: room.roomTitle,
              total: 0,
              products: [],
            };
            whEntry.rooms.set(room.rid, roomEntry);
          }
          roomEntry.total += room.count;
          // Carry the fields the ExpiryEditorModal needs (defaultExpiryDays)
          // and the totals so the Manage-Expiry button can be disabled for
          // products with zero physical tags even in this pivot view.
          roomEntry.products.push({
            epc: p.epc,
            name: p.name,
            count: room.count,
            productImgLink: p.productImgLink,
            defaultExpiryDays: p.defaultExpiryDays ?? null,
            total: p.total,
          });
        }
      }
    }
  }
  // Flatten to arrays, sorted by count desc.
  return [...locMap.values()]
    .map((loc) => ({
      ...loc,
      warehouses: [...loc.warehouses.values()]
        .map((wh) => ({
          ...wh,
          rooms: [...wh.rooms.values()]
            .map((room) => ({
              ...room,
              products: [...room.products].sort((a, b) => b.count - a.count),
            }))
            .sort((a, b) => b.total - a.total),
        }))
        .sort((a, b) => b.total - a.total),
    }))
    .sort((a, b) => b.total - a.total);
};

const ByLocationView = ({ products, searchTerm, onManageExpiry }) => {
  const locTree = useMemo(() => buildLocationTree(products), [products]);

  // Filter by product search: only keep rooms that contain at least one
  // matching product. Empty filter = show everything.
  const q = searchTerm.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return locTree;
    const match = (epc, name) =>
      (name || "").toLowerCase().includes(q) || (epc || "").toLowerCase().includes(q);
    return locTree
      .map((loc) => ({
        ...loc,
        warehouses: loc.warehouses
          .map((wh) => ({
            ...wh,
            rooms: wh.rooms
              .map((room) => ({
                ...room,
                products: room.products.filter((pr) => match(pr.epc, pr.name)),
              }))
              .filter((room) => room.products.length > 0),
          }))
          .filter((wh) => wh.rooms.length > 0),
      }))
      .filter((loc) => loc.warehouses.length > 0);
  }, [locTree, q]);

  if (filtered.length === 0) {
    return (
      <div className="empty-state">
        <i className="fa fa-map-o empty-state-icon" />
        <p>{q ? `No hierarchy nodes contain products matching "${q}"` : "No physical tags located in any room yet."}</p>
      </div>
    );
  }

  return (
    <div className="pl-by-location">
      {filtered.map((loc, li) => (
        <details key={loc.locationId || `loc-${li}`} className="pl-loc-card" open>
          <summary className="pl-loc-summary">
            <i className="fa fa-map-marker pl-tree-icon" aria-hidden />
            <span className="pl-loc-title">{loc.locationName}</span>
            <span className="pl-loc-count">{loc.total} tag{loc.total === 1 ? "" : "s"}</span>
          </summary>
          <div className="pl-loc-children">
            {loc.warehouses.map((wh, wi) => (
              <details key={wh.warehouseId || `wh-${wi}`} className="pl-wh-card" open>
                <summary className="pl-wh-summary">
                  <i className="fa fa-building pl-tree-icon" aria-hidden />
                  <span className="pl-wh-title">{wh.warehouseName}</span>
                  <span className="pl-wh-count">{wh.total}</span>
                </summary>
                <div className="pl-wh-children">
                  {wh.rooms.map((room) => (
                    <div key={room.rid} className="pl-room-card">
                      <div className="pl-room-header">
                        <i className="fa fa-cube pl-tree-icon" aria-hidden />
                        <span className="pl-room-title">{room.roomTitle}</span>
                        <span className="pl-room-count">{room.total}</span>
                      </div>
                      <table className="tags-table pl-inner-table">
                        <thead>
                          <tr>
                            <th style={{ width: 42 }}></th>
                            <th>Product</th>
                            <th>EPC</th>
                            <th style={{ textAlign: "right", width: 90 }}>Count</th>
                            <th style={{ textAlign: "right", width: 150 }}>Expiry</th>
                          </tr>
                        </thead>
                        <tbody>
                          {room.products.map((pr) => (
                            <tr key={pr.epc}>
                              <td>
                                {pr.productImgLink ? (
                                  <img
                                    src={pr.productImgLink}
                                    alt={pr.name}
                                    style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border-primary)" }}
                                  />
                                ) : (
                                  <span style={{ color: "var(--text-tertiary)" }}>—</span>
                                )}
                              </td>
                              <td><span className="epc-cell fw-bold">{pr.name}</span></td>
                              <td><span className="tag-badge" style={{ fontFamily: "monospace" }}>{pr.epc}</span></td>
                              <td style={{ textAlign: "right" }}>
                                <span className="tag-badge" style={{ fontWeight: 600 }}>{pr.count}</span>
                              </td>
                              <td style={{ textAlign: "right" }}>
                                <button
                                  type="button"
                                  className="pl-expiry-btn"
                                  onClick={() => onManageExpiry?.(pr)}
                                  disabled={!onManageExpiry || (pr.total ?? pr.count) === 0}
                                  title={(pr.total ?? pr.count) === 0
                                    ? "No physical tags to manage"
                                    : "Manage end dates for every tag of this product"}
                                >
                                  <i className="fa fa-calendar" aria-hidden /> Manage
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
};

// --------------- Main page component ---------------------------------------

const ProductList = () => {
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewTab, setViewTab] = useState("byProduct"); // "byProduct" | "byLocation"
  const [searchTerm, setSearchTerm] = useState("");
  const [expiryTarget, setExpiryTarget] = useState(null); // product currently being edited

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await api.getProductInventory();
      setData(payload);
    } catch (error) {
      showToast(error.message || "Failed to fetch inventory overview", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const totals = data?.totals || {};
  const products = data?.products || [];
  const unassigned = data?.unassigned;
  const orphans = data?.orphans;

  // Deep-link from the Expiry Log: /product-list?expiryFor=<EPC> auto-opens
  // the Manage-Expiry modal for that product once inventory loads. Strips
  // the query string afterwards so refresh/back doesn't re-trigger.
  useEffect(() => {
    if (!products.length) return;
    const qs = new URLSearchParams(location.search);
    const wantedEpc = (qs.get("expiryFor") || "").trim().toUpperCase();
    if (!wantedEpc) return;
    const match = products.find((p) => (p.epc || "").toUpperCase() === wantedEpc);
    if (match) {
      setExpiryTarget(match);
      navigate("/product-list", { replace: true });
    } else {
      showToast(`No product with EPC ${wantedEpc} in inventory.`, 3000, true, "warning");
      navigate("/product-list", { replace: true });
    }
  }, [products, location.search, navigate, showToast]);

  const hasUnassigned = (unassigned?.total || 0) > 0;
  const hasOrphans = (orphans?.total || 0) > 0;

  return (
    <div className="products-page product-list-page">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Inventory</h1>
            <p className="dashboard-subtitle">
              Live count of every physical tag by product, location, warehouse, and room.
            </p>
          </div>
          <Button
            value={loading ? "Refreshing..." : "Refresh"}
            iconLeft={loading ? "fa-spinner fa-spin" : "fa-refresh"}
            onClick={fetchInventory}
            variant="outline"
            disabled={loading}
          />
        </div>

        {/* Summary cards */}
        <div className="pl-summary-grid">
          <SummaryCard icon="fa-tags"        label="Physical tags"    value={totals.tags ?? "—"}        accent="#3B82F6" />
          <SummaryCard icon="fa-cubes"       label="Products stocked" value={`${totals.products ?? 0}${totals.catalogProducts ? ` / ${totals.catalogProducts}` : ""}`} accent="#8B5CF6" />
          <SummaryCard icon="fa-map-marker"  label="Locations"        value={totals.locations ?? "—"}   accent="#10B981" />
          <SummaryCard icon="fa-building"    label="Warehouses"       value={totals.warehouses ?? "—"}  accent="#F59E0B" />
          <SummaryCard icon="fa-cube"        label="Rooms"            value={totals.rooms ?? "—"}       accent="#EC4899" />
        </div>

        {/* Filters + tab switcher */}
        <div className="filters-section">
          <div className="filters-grid pl-filters-grid">
            <TextField
              placeholder="Search by product name or EPC..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="pl-tabs">
              <button
                type="button"
                className={`pl-tab ${viewTab === "byProduct" ? "active" : ""}`}
                onClick={() => setViewTab("byProduct")}
              >
                <i className="fa fa-cubes" aria-hidden /> By Product
              </button>
              <button
                type="button"
                className={`pl-tab ${viewTab === "byLocation" ? "active" : ""}`}
                onClick={() => setViewTab("byLocation")}
              >
                <i className="fa fa-map-marker" aria-hidden /> By Location
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="table-section">
          <div className="section-header">
            <h2 className="section-title">
              {viewTab === "byProduct" ? "Products" : "Hierarchy"}
              <span className="results-count" style={{ marginLeft: 8 }}>
                {viewTab === "byProduct" ? `(${products.length})` : ""}
              </span>
            </h2>
          </div>
          <div className="table-container">
            {loading && !data ? (
              <div className="loading-state">
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "32px", color: "var(--text-tertiary)" }} />
                <p>Loading inventory...</p>
              </div>
            ) : viewTab === "byProduct" ? (
              <ByProductView
                products={products}
                searchTerm={searchTerm}
                onManageExpiry={(p) => setExpiryTarget(p)}
              />
            ) : (
              <ByLocationView
                products={products}
                searchTerm={searchTerm}
                onManageExpiry={(p) => setExpiryTarget(p)}
              />
            )}
          </div>
        </div>

        {/* Exceptions */}
        {(hasUnassigned || hasOrphans) && (
          <div className="pl-exceptions">
            {hasUnassigned && (
              <div className="pl-exception-card pl-exception-warn">
                <div className="pl-exception-header">
                  <i className="fa fa-question-circle" aria-hidden />
                  <span>Unassigned tags</span>
                  <span className="pl-exception-count">{unassigned.total}</span>
                </div>
                <p className="pl-subtle">
                  Physical tags that have no room assigned. They exist in the system but aren't placed anywhere yet.
                </p>
                <StateBadges states={unassigned.states} />
              </div>
            )}
            {hasOrphans && (
              <div className="pl-exception-card pl-exception-danger">
                <div className="pl-exception-header">
                  <i className="fa fa-exclamation-triangle" aria-hidden />
                  <span>Tags not in catalog</span>
                  <span className="pl-exception-count">{orphans.total}</span>
                </div>
                <p className="pl-subtle">
                  Scanned tag EPCs that aren't registered as product types yet. Register them from the Products page.
                </p>
                <StateBadges states={orphans.states} />
                {orphans.byEpc?.length > 0 && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--text-secondary)" }}>
                      Show {orphans.byEpc.length} EPC{orphans.byEpc.length === 1 ? "" : "s"}
                    </summary>
                    <ul className="pl-orphan-list">
                      {orphans.byEpc.map((o) => (
                        <li key={o.epc}>
                          <span className="tag-badge" style={{ fontFamily: "monospace" }}>{o.epc}</span>
                          <span className="pl-orphan-count">{o.total}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        )}

        {/* Expiry editor modal — opened per-product via the Manage button. */}
        {expiryTarget && (
          <ExpiryEditorModal
            product={expiryTarget}
            onClose={() => setExpiryTarget(null)}
            onSaved={() => {
              // No need to refetch the whole inventory — endDate doesn't
              // affect totals/hierarchy. The user sees the new value in
              // the modal itself until they close it.
            }}
          />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ExpiryEditorModal — lets an admin set/change the end date of every physical
// tag for a product. Each row has a native date picker plus three quick-offset
// buttons (+30 days, +6 months, +1 year) that bump the field relative to the
// tag's CURRENT end date (or today, if none).
// ---------------------------------------------------------------------------
const ExpiryEditorModal = ({ product, onClose, onSaved }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);   // [{ ...tag, draftDate }]
  const [loading, setLoading] = useState(false);
  const [savingTid, setSavingTid] = useState(null);

  // Clicking a TID closes the modal and jumps to the full details page —
  // mirrors the Dashboard's "click a tag name" flow.
  const openTagDetails = (tid) => {
    if (!tid) return;
    onClose?.();
    navigate(`/tags/${encodeURIComponent(tid)}`);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTagsByProductEpc(product.epc);
      setItems(
        (res.items || []).map((t) => ({ ...t, draftDate: toDateInputValue(t.endDate) }))
      );
    } catch (err) {
      showToast(err.message || "Failed to load tags", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  }, [product.epc, showToast]);

  useEffect(() => { load(); }, [load]);

  const updateDraft = (tid, draftDate) => {
    setItems((prev) => prev.map((t) => (t.tid === tid ? { ...t, draftDate } : t)));
  };

  const offsetFrom = (baseIso, amount, unit) => {
    const base = baseIso ? new Date(baseIso) : new Date();
    if (isNaN(base.getTime())) return new Date();
    const next = new Date(base.getTime());
    if (unit === "days")   next.setDate(next.getDate() + amount);
    if (unit === "months") next.setMonth(next.getMonth() + amount);
    if (unit === "years")  next.setFullYear(next.getFullYear() + amount);
    return next;
  };

  const applyOffset = (tid, amount, unit) => {
    setItems((prev) => prev.map((t) => {
      if (t.tid !== tid) return t;
      const baseIso = t.draftDate || t.endDate;
      return { ...t, draftDate: toDateInputValue(offsetFrom(baseIso, amount, unit)) };
    }));
  };

  // Save a single row. Calls PUT /tags/:tid with just `endDate`, which the
  // backend logs as a dedicated TAG_ENDDATE_SET / TAG_ENDDATE_CLEARED event.
  const saveOne = async (tid) => {
    const item = items.find((t) => t.tid === tid);
    if (!item) return;
    const isoOrNull = item.draftDate ? new Date(item.draftDate).toISOString() : null;
    setSavingTid(tid);
    try {
      await api.setTagEndDate(tid, isoOrNull);
      showToast(
        isoOrNull ? `End date saved for ${tid}` : `End date cleared for ${tid}`,
        2200, true, "success"
      );
      setItems((prev) => prev.map((t) => (t.tid === tid ? {
        ...t,
        endDate: isoOrNull,
        daysRemaining: isoOrNull ? Math.round((new Date(isoOrNull).getTime() - Date.now()) / 86400000) : null,
        expired: isoOrNull ? new Date(isoOrNull).getTime() < Date.now() : false,
      } : t)));
      onSaved?.();
    } catch (err) {
      showToast(err.message || "Failed to save", 3000, true, "danger");
    } finally {
      setSavingTid(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !savingTid && onClose()}>
      <div
        className="modal-container pl-expiry-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 820, width: "92vw" }}
      >
        <div className="modal-header-wrapper">
          <div className="modal-icon-circle" style={{ backgroundColor: "#DBEAFE", color: "#1E40AF" }}>
            <i className="fa fa-calendar" />
          </div>
          <div className="modal-title-section">
            <span className="modal-title">End dates · {product.name}</span>
            <span className="modal-subtitle">
              Pick a date for any physical tag, or use the quick offsets. Changes save per-row so you
              never lose work.
            </span>
          </div>
          <div className="modal-close-btn" onClick={onClose}>
            <i className="fa fa-times" />
          </div>
        </div>
        {/* Product's default-expiry strip. Purely informational here — this
            modal only edits per-tag end dates. Creating a NEW tag of this
            product type will start with this default automatically. */}
        {typeof product.defaultExpiryDays === "number" && product.defaultExpiryDays >= 0 ? (
          <div className="pl-expiry-default-strip pl-expiry-default-set">
            <i className="fa fa-info-circle" aria-hidden />
            <span>
              <strong>Default expiry:</strong> {product.defaultExpiryDays} day{product.defaultExpiryDays === 1 ? "" : "s"} from creation.
              New tags of <em>{product.name}</em> inherit it; existing tags below are unchanged.
            </span>
          </div>
        ) : (
          <div className="pl-expiry-default-strip pl-expiry-default-none">
            <i className="fa fa-info-circle" aria-hidden />
            <span>
              No default expiry set on this product type. Set one on the Products page to auto-fill new tags.
            </span>
          </div>
        )}

        <div className="modal-body-wrapper" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {loading ? (
            <div className="loading-state" style={{ padding: 24 }}>
              <i className="fa fa-spinner fa-spin" style={{ fontSize: 24, color: "var(--text-tertiary)" }} />
              <p>Loading tags…</p>
            </div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <i className="fa fa-tag empty-state-icon" />
              <p>No physical tags exist for this product yet.</p>
            </div>
          ) : (
            <div className="pl-expiry-list">
              {items.map((t) => {
                const days = daysUntil(t.draftDate || t.endDate);
                const badge = days == null
                  ? { label: "No end date", cls: "none" }
                  : days < 0
                    ? { label: `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`, cls: "expired" }
                    : days <= 30
                      ? { label: `${days} day${days === 1 ? "" : "s"} left`, cls: "soon" }
                      : { label: `${days} day${days === 1 ? "" : "s"} left`, cls: "ok" };
                return (
                  <div key={t.tid} className={`pl-expiry-row pl-expiry-${badge.cls}`}>
                    <div className="pl-expiry-main">
                      <button
                        type="button"
                        className="pl-expiry-tid pl-expiry-tid-link"
                        onClick={() => openTagDetails(t.tid)}
                        disabled={!t.tid}
                        title={t.tid ? "Open tag details" : undefined}
                      >
                        <i className="fa fa-tag" aria-hidden />
                        <span>{t.tid || "—"}</span>
                        <i className="fa fa-external-link pl-expiry-tid-arrow" aria-hidden />
                      </button>
                      <div className="pl-expiry-meta">
                        {t.roomTitle && (
                          <span className="pl-expiry-chip">
                            <i className="fa fa-cube" aria-hidden /> {t.roomTitle}
                          </span>
                        )}
                        <span className={`pl-expiry-badge pl-expiry-badge-${badge.cls}`}>{badge.label}</span>
                      </div>
                    </div>
                    <div className="pl-expiry-controls">
                      <input
                        type="date"
                        className="pl-expiry-input"
                        value={t.draftDate || ""}
                        onChange={(e) => updateDraft(t.tid, e.target.value)}
                        disabled={savingTid === t.tid}
                      />
                      <div className="pl-expiry-offsets">
                        <button type="button" onClick={() => applyOffset(t.tid, 30, "days")}>+30 d</button>
                        <button type="button" onClick={() => applyOffset(t.tid, 6, "months")}>+6 mo</button>
                        <button type="button" onClick={() => applyOffset(t.tid, 1, "years")}>+1 y</button>
                      </div>
                      {t.draftDate && (
                        <button
                          type="button"
                          className="pl-expiry-clear-btn"
                          onClick={() => updateDraft(t.tid, "")}
                          disabled={savingTid === t.tid}
                          title="Clear the draft date"
                        >
                          <i className="fa fa-eraser" aria-hidden />
                        </button>
                      )}
                      <button
                        type="button"
                        className="pl-expiry-save-btn"
                        onClick={() => saveOne(t.tid)}
                        disabled={savingTid === t.tid || (t.draftDate || "") === toDateInputValue(t.endDate)}
                        title={(t.draftDate || "") === toDateInputValue(t.endDate) ? "No changes to save" : "Save this end date"}
                      >
                        {savingTid === t.tid ? (
                          <i className="fa fa-spinner fa-spin" aria-hidden />
                        ) : (
                          <><i className="fa fa-check" aria-hidden /> Save</>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer-wrapper">
          <Button value="Close" iconLeft="fa-times" variant="outline" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default ProductList;
